
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Trash2, MicOff, Pencil, Check } from "lucide-react";
import useTranscribeMic from "@/hooks/use-transcribe";
import RichTextDisplay from "./RichTextDisplay";

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
}

const Tooltip: React.FC<TooltipProps> = ({ children, content, side = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sideClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2'
  };
  const arrowClasses = {
    top: 'top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-700',
    bottom: 'bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-700',
    left: 'left-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent border-l-gray-700',
    right: 'right-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-gray-700'
  };
  
  return (
    <div 
      className="relative inline-block" 
      onMouseEnter={() => setIsVisible(true)} 
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className={`absolute z-50 ${sideClasses[side]} pointer-events-none`}>
          <div className="bg-gray-700 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
            {content}
          </div>
          <div className={`absolute w-0 h-0 ${arrowClasses[side]}`} />
        </div>
      )}
    </div>
  );
};

interface SectionRendererProps {
  sectionId: string;
  title: string;
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  showSearch?: boolean;
  dictationPhase: 'general' | 'refine';
}

const SectionRenderer: React.FC<SectionRendererProps> = ({
  sectionId,
  title,
  value,
  onValueChange,
  placeholder,
  showSearch = false,
  dictationPhase,
}) => {
  const { isTranscribing, currentTranscript, transcribedText, startTranscription, stopTranscription, clearTranscribedText } = useTranscribeMic();

  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isTranscribing) {
      const liveText = [
        value,
        ...transcribedText.map(t => t.content),
        currentTranscript
      ].filter(Boolean).join(' ').trim().replace(/\s+/g, ' ');
      setLocalValue(liveText);
    }
  }, [isTranscribing, currentTranscript, transcribedText, value]);
  
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [localValue]);

  useEffect(() => {
    if (!isTranscribing && transcribedText.length > 0) {
      const finalContent = [
        value, 
        ...transcribedText.map(t => t.content)
      ].filter(Boolean).join(' ').trim().replace(/\s+/g, ' ');
      
      if (finalContent && finalContent !== value) {
        onValueChange(finalContent);
      }
      clearTranscribedText();
    }
  }, [isTranscribing, transcribedText, value, onValueChange, clearTranscribedText]);

  const handleMicToggle = () => {
    if (isTranscribing) {
      stopTranscription();
    } else {
      startTranscription();
    }
  };
  
  const handleClearSection = () => {
    if (isTranscribing) {
      stopTranscription();
    }
    clearTranscribedText();
    onValueChange("");
  };

  const handleManualTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value);
    onValueChange(e.target.value);
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Save and exit edit mode
      onValueChange(localValue);
    }
    setIsEditing(!isEditing);
  };

  // Capture content height before switching to edit mode
  useEffect(() => {
    if (contentRef.current && !isEditing) {
      setContentHeight(contentRef.current.offsetHeight);
    }
  }, [localValue, isEditing]);

  // Auto-focus textarea when entering edit mode and set its height
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
      // Set minimum height to match previous content
      if (contentHeight) {
        textareaRef.current.style.minHeight = `${contentHeight}px`;
      }
    }
  }, [isEditing, contentHeight]);

  const showRichText = dictationPhase === 'refine' && !isEditing && !isTranscribing && localValue;

  return (
    <div className="group py-3 sm:py-4">
      {/* Mobile-first responsive layout */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 px-3 sm:px-4">
        {/* Section title - full width on mobile, fixed width on desktop */}
        <div className="flex-shrink-0 sm:w-32">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 sm:mb-0 sm:pt-3">
            {title}
          </h3>
        </div>

        {/* Content area with controls */}
        <div className="flex-1 relative">
          <div className={`flex items-start gap-2 border rounded-lg p-3 transition-colors ${
            isEditing ? 'border-blue-400 bg-blue-50/30' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300'
          }`}>
            {/* Content - Rich text display or Textarea */}
            <div className="flex-1 relative">
              {showRichText ? (
                <div
                  ref={contentRef}
                  className="min-h-[40px] cursor-pointer transition-colors"
                  onClick={() => setIsEditing(true)}
                >
                  <RichTextDisplay content={localValue} className="text-sm" />
                </div>
              ) : (
                <>
                  <textarea
                    ref={textareaRef}
                    value={localValue}
                    onChange={handleManualTextChange}
                    className="w-full bg-transparent border-0 outline-none resize-none text-gray-700 placeholder:text-gray-400 min-h-[40px] text-sm leading-relaxed transition-all overflow-hidden mobile-touch-target"
                    placeholder={placeholder}
                    rows={3}
                    disabled={isTranscribing}
                    style={{ minHeight: contentHeight ? `${contentHeight}px` : '40px' }}
                    onBlur={() => {
                      if (isEditing) {
                        setIsEditing(false);
                        onValueChange(localValue);
                      }
                    }}
                  />
                  {isTranscribing && (
                    <div className="absolute top-2 right-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Controls - always visible on mobile in refine phase, hidden otherwise */}
            <div className={`flex items-center gap-1 sm:gap-2 transition-opacity duration-200 flex-shrink-0
                ${dictationPhase === 'refine'
                  ? 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100'
                  : 'opacity-0'
                }
                ${isTranscribing || isEditing ? '!opacity-100' : ''}
            `}>
              {/* Edit/Done toggle button */}
              {dictationPhase === 'refine' && localValue && (
                <Tooltip content={isEditing ? "Done editing" : "Edit text"} side="top">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-8 w-8 rounded-md transition-all duration-200 mobile-touch-target ${
                      isEditing
                        ? "bg-green-500 text-white hover:bg-green-600"
                        : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                    }`}
                    onClick={handleEditToggle}
                  >
                    {isEditing ? <Check size={14} /> : <Pencil size={14} />}
                  </Button>
                </Tooltip>
              )}
              <Tooltip content={isTranscribing ? "Stop recording" : "Start voice input"} side="top">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-8 w-8 rounded-md transition-all duration-200 mobile-touch-target ${
                    isTranscribing
                      ? "bg-blue-500 text-white hover:bg-blue-600"
                      : "text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                  }`}
                  onClick={handleMicToggle}
                >
                  {isTranscribing ? <MicOff size={14} /> : <Mic size={14} />}
                </Button>
              </Tooltip>
              <Tooltip content="Delete content" side="top">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSection}
                  className="h-8 w-8 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 mobile-touch-target"
                >
                  <Trash2 size={14} />
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionRenderer;
